import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import API from '../api';
import { toast } from 'react-toastify';
import { formatAdToBs } from '../utils/nepaliDate';
import '../styles/global.css';

export default function StaffManagementPage() {
  const { user } = useAuth();
  
  const getCache = (key, defaultVal) => {
    const cached = sessionStorage.getItem(`staffManagement_${key}`);
    return cached ? JSON.parse(cached) : defaultVal;
  };
  
  const [activeTab, setActiveTab] = useState(() => getCache('activeTab', 'directory'));
  
  const [staffList, setStaffList] = useState(() => getCache('staffList', []));
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [availablePermissions, setAvailablePermissions] = useState(() => getCache('availablePermissions', []));
  const [searchQuery, setSearchQuery] = useState(() => getCache('searchQuery', ''));
  const [filterStatus, setFilterStatus] = useState(() => getCache('filterStatus', 'all'));
  
  const [formData, setFormData] = useState({
    name: '', email: '', phone: '', password: '', permissions: [], baseSalary: '', allowance: '', deduction: '', gender: 'Male', shift: 'Morning'
  });

  const [attendanceRecords, setAttendanceRecords] = useState(() => getCache('attendanceRecords', []));
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const cached = getCache('selectedMonth', null);
    if (cached) return cached;
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [attendanceStaffId, setAttendanceStaffId] = useState(() => getCache('attendanceStaffId', 'all'));
  const [showManualAttendanceModal, setShowManualAttendanceModal] = useState(false);
  const [manualAttendance, setManualAttendance] = useState({
    user: '', date: new Date().toISOString().split('T')[0], checkIn: '', checkOut: '', status: 'Present', notes: ''
  });

  const [payrollMonth, setPayrollMonth] = useState(() => {
    const cached = getCache('payrollMonth', null);
    if (cached) return cached;
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [payslips, setPayslips] = useState(() => getCache('payslips', []));
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutSlip, setPayoutSlip] = useState(null);
  const [payoutDetails, setPayoutDetails] = useState({ paymentType: 'CASH', notes: '' });

  const [leavesList, setLeavesList] = useState(() => getCache('leavesList', []));
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [leaveFormData, setLeaveFormData] = useState({
    startDate: new Date().toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0], type: 'Unpaid', reason: ''
  });

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [isTablet, setIsTablet] = useState(window.innerWidth <= 1024 && window.innerWidth > 768);
  const [isSmallMobile, setIsSmallMobile] = useState(window.innerWidth <= 480);

  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setIsMobile(width <= 768);
      setIsTablet(width <= 1024 && width > 768);
      setIsSmallMobile(width <= 480);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    sessionStorage.setItem('staffManagement_activeTab', JSON.stringify(activeTab));
  }, [activeTab]);

  useEffect(() => {
    sessionStorage.setItem('staffManagement_searchQuery', JSON.stringify(searchQuery));
  }, [searchQuery]);

  useEffect(() => {
    sessionStorage.setItem('staffManagement_filterStatus', JSON.stringify(filterStatus));
  }, [filterStatus]);

  useEffect(() => {
    sessionStorage.setItem('staffManagement_selectedMonth', JSON.stringify(selectedMonth));
  }, [selectedMonth]);

  useEffect(() => {
    sessionStorage.setItem('staffManagement_attendanceStaffId', JSON.stringify(attendanceStaffId));
  }, [attendanceStaffId]);

  useEffect(() => {
    sessionStorage.setItem('staffManagement_payrollMonth', JSON.stringify(payrollMonth));
  }, [payrollMonth]);

  const fetchStaff = useCallback(async () => {
    try {
      if (!sessionStorage.getItem('staffManagement_staffList')) setLoading(true);
      const { data } = await API.get('/staff');
      setStaffList(data.staff || []);
      sessionStorage.setItem('staffManagement_staffList', JSON.stringify(data.staff || []));
    } catch (error) {
      toast.error('Failed to fetch staff list');
    } finally { setLoading(false); }
  }, []);

  const fetchPermissions = useCallback(async () => {
    try {
      const { data } = await API.get('/staff/permissions');
      setAvailablePermissions(data.permissions || []);
      sessionStorage.setItem('staffManagement_availablePermissions', JSON.stringify(data.permissions || []));
    } catch (error) {
      const defaultPermissions = [
        { key: 'dashboard', label: 'Dashboard', description: 'View dashboard and analytics', icon: '📊' },
        { key: 'parties', label: 'Parties', description: 'Manage suppliers and customers', icon: '🤝' },
        { key: 'transactions', label: 'Transactions', description: 'View and manage transactions', icon: '💳' },
        { key: 'medicines', label: 'Medicines', description: 'Manage inventory and medicines', icon: '💊' },
        { key: 'billing', label: 'Billing', description: 'Create and manage bills', icon: '🧾' },
        { key: 'sales', label: 'Sales', description: 'View sales reports', icon: '📈' },
        { key: 'expiry', label: 'Expiry', description: 'View expiring medicines', icon: '⏰' },
        { key: 'contact', label: 'Contact', description: 'Access contact/support', icon: '📞' },
        { key: 'profile', label: 'Profile', description: 'View own profile', icon: '👤' },
        { key: 'reports', label: 'Reports', description: 'Generate and view reports', icon: '📋' }
      ];
      setAvailablePermissions(defaultPermissions);
      sessionStorage.setItem('staffManagement_availablePermissions', JSON.stringify(defaultPermissions));
    }
  }, []);

  useEffect(() => {
    fetchStaff();
    if (availablePermissions.length === 0) fetchPermissions();
  }, [fetchStaff, fetchPermissions]);

  const fetchAttendance = useCallback(async () => {
    try {
      if (!sessionStorage.getItem('staffManagement_attendanceRecords')) setLoading(true);
      const params = { month: selectedMonth };
      if (attendanceStaffId !== 'all') params.staffId = attendanceStaffId;
      const { data } = await API.get('/hr/attendance', { params });
      setAttendanceRecords(data.records || []);
      sessionStorage.setItem('staffManagement_attendanceRecords', JSON.stringify(data.records || []));
    } catch (err) { 
      toast.error('Failed to load attendance records'); 
    } finally {
      setLoading(false);
    }
  }, [selectedMonth, attendanceStaffId]);

  const fetchPayslips = useCallback(async () => {
    try {
      if (!sessionStorage.getItem('staffManagement_payslips')) setLoading(true);
      const { data } = await API.get('/hr/payroll/slips', { params: { month: payrollMonth } });
      setPayslips(data.payslips || []);
      sessionStorage.setItem('staffManagement_payslips', JSON.stringify(data.payslips || []));
    } catch (err) { 
      toast.error('Failed to load payslips'); 
    } finally {
      setLoading(false);
    }
  }, [payrollMonth]);

  const fetchLeaves = useCallback(async () => {
    try {
      if (!sessionStorage.getItem('staffManagement_leavesList')) setLoading(true);
      const { data } = await API.get('/hr/leaves');
      setLeavesList(data || []);
      sessionStorage.setItem('staffManagement_leavesList', JSON.stringify(data || []));
    } catch (err) { 
      toast.error('Failed to load leave requests'); 
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/hr/leaves', leaveFormData);
      toast.success('Leave request submitted successfully.');
      setShowLeaveModal(false);
      fetchLeaves();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to submit leave request'); }
  };

  const handleUpdateLeaveStatus = async (leaveId, status) => {
    try {
      await API.put(`/hr/leaves/${leaveId}`, { status });
      toast.success(`Leave request was ${status.toLowerCase()}`);
      fetchLeaves();
    } catch (err) { toast.error('Failed to update leave status'); }
  };

  useEffect(() => {
    if (activeTab === 'attendance') fetchAttendance();
    if (activeTab === 'payroll') fetchPayslips();
    if (activeTab === 'leaves') fetchLeaves();
  }, [activeTab, fetchAttendance, fetchPayslips, fetchLeaves]);

  const handleCheckIn = async () => {
    try {
      const { data } = await API.post('/hr/attendance/check-in');
      toast.success(data.message || 'Clocked in successfully');
      if (activeTab === 'attendance') fetchAttendance();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to check-in'); }
  };

  const handleCheckOut = async () => {
    try {
      const { data } = await API.post('/hr/attendance/check-out');
      toast.success(data.message || 'Clocked out successfully');
      if (activeTab === 'attendance') fetchAttendance();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to check-out'); }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    try {
      if (modalMode === 'create') {
        const res = await API.post('/staff', {
          name: formData.name, email: formData.email, phone: formData.phone, password: formData.password, permissions: formData.permissions
        });
        const staffId = res.data.staff.id;
        await API.put(`/hr/staff/${staffId}/shift`, { shift: formData.shift || 'Morning', gender: formData.gender || 'Male' });
        await API.put(`/hr/salary-config/${staffId}`, { baseSalary: Number(formData.baseSalary) || 0, allowance: Number(formData.allowance) || 0, deduction: Number(formData.deduction) || 0 });
        toast.success('Staff member registered successfully');
      } else {
        await API.put(`/staff/${selectedStaff._id}`, { name: formData.name, email: formData.email, phone: formData.phone, permissions: formData.permissions });
        await API.put(`/hr/staff/${selectedStaff._id}/shift`, { shift: formData.shift || 'Morning', gender: formData.gender || 'Male' });
        await API.put(`/hr/salary-config/${selectedStaff._id}`, { baseSalary: Number(formData.baseSalary) || 0, allowance: Number(formData.allowance) || 0, deduction: Number(formData.deduction) || 0 });
        toast.success('Staff details updated successfully');
      }
      setShowModal(false);
      fetchStaff();
    } catch (err) { toast.error(err.response?.data?.message || 'Failed to save staff'); }
  };

  const handleEditStaff = (staff) => {
    setSelectedStaff(staff);
    setModalMode('edit');
    const salary = staff.salaryConfig || { baseSalary: '', allowance: '', deduction: '' };
    setFormData({
      name: staff.name, email: staff.email, phone: staff.phone, password: '', permissions: staff.permissions || [],
      baseSalary: salary.baseSalary || '', allowance: salary.allowance || '', deduction: salary.deduction || '',
      gender: staff.gender || 'Male', shift: staff.shift || 'Morning'
    });
    setShowModal(true);
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) return;
    try {
      await API.delete(`/staff/${id}`);
      toast.success('Staff deleted successfully');
      fetchStaff();
    } catch (err) { toast.error('Failed to delete staff member'); }
  };

  const toggleStaffStatus = async (id) => {
    try {
      await API.patch(`/staff/${id}/toggle-status`);
      toast.success('Status toggled successfully');
      fetchStaff();
    } catch (err) { toast.error('Failed to toggle status'); }
  };

  const handleGeneratePayroll = async () => {
    try {
      await API.post('/hr/payroll/generate', { month: payrollMonth });
      toast.success('Monthly payroll generated');
      fetchPayslips();
    } catch (err) { toast.error(err.response?.data?.message || 'Error generating payroll'); }
  };

  const handleConfirmPayout = async (e) => {
    e.preventDefault();
    try {
      await API.post(`/hr/payroll/pay/${payoutSlip._id}`, payoutDetails);
      toast.success('Payslip marked as paid. Expense logged.');
      setShowPayoutModal(false);
      setPayoutSlip(null);
      setPayoutDetails({ paymentType: 'CASH', notes: '' });
      fetchPayslips();
    } catch (err) { toast.error(err.response?.data?.message || 'Error recording payout'); }
  };

  const handleDeletePayslip = async (id) => {
    if (!window.confirm('Are you sure you want to delete this unpaid payslip?')) return;
    try {
      await API.delete(`/hr/payroll/slips/${id}`);
      toast.success('Payslip deleted');
      fetchPayslips();
    } catch (err) { toast.error('Failed to delete payslip'); }
  };

  const handleManualAttendanceSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.put('/hr/attendance', manualAttendance);
      toast.success('Attendance record updated');
      setShowManualAttendanceModal(false);
      fetchAttendance();
    } catch (err) { toast.error('Failed to adjust attendance'); }
  };

  const printPayslip = (slip) => {
    const printWindow = window.open('', '_blank');
    const bsMonthStr = formatAdToBs(slip.month ? `${slip.month}-01` : new Date(), 'full');
    const content = `
      <html>
        <head>
          <title>Payslip - ${slip.userName}</title>
          <style>
            body { font-family: Inter, sans-serif; padding: 30px; color: #1a202c; }
            .header { text-align: center; border-bottom: 2px solid #e0f2fe; padding-bottom: 12px; margin-bottom: 20px; }
            .details { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; margin-bottom: 25px; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; }
            th, td { border-bottom: 1px solid #cbd5e1; padding: 10px; text-align: left; }
            th { background: #f4f9fd; color: #64748b; }
            .total { font-size: 16px; font-weight: 700; color: #1880c2; background: #f4f9fd; }
            .signature { margin-top: 50px; display: flex; justify-content: space-between; }
          </style>
        </head>
        <body>
          <div class="header">
            <h2 style="color: #1880c2;">Salary Slip / Advice</h2>
            <p style="margin:4px 0;">Month: <strong>${slip.month}</strong> (${bsMonthStr})</p>
            <p style="margin:4px 0; font-size:12px; color:#64748b;">Status: <strong>${slip.status}</strong></p>
          </div>
          <div class="details">
            <div><strong>Employee:</strong> ${slip.userName} (${slip.user?.staffId || 'STF'})</div>
            <div><strong>Date:</strong> ${slip.paymentDate ? new Date(slip.paymentDate).toLocaleDateString('en-IN') : 'Draft Advice'}</div>
            <div><strong>Payment Mode:</strong> ${slip.paymentType || 'CASH / Bank'}</div>
            <div><strong>Slip ID:</strong> ${slip._id}</div>
          </div>
          <table>
            <thead>
              <tr><th>Component</th><th style="text-align: right;">Amount</th></tr>
            </thead>
            <tbody>
              <tr><td>Base Salary</td><td style="text-align: right;">Rs ${(slip.baseSalary || 0).toFixed(2)}</td></tr>
              <tr><td>Allowances</td><td style="text-align: right; color: #10b981;">+ Rs ${(slip.allowances || 0).toFixed(2)}</td></tr>
              <tr><td>Deductions / Unpaid Leave</td><td style="text-align: right; color: #ef4444;">- Rs ${(slip.deductions || 0).toFixed(2)}</td></tr>
              <tr class="total"><td>Net Payable Amount</td><td style="text-align: right;">Rs ${(slip.netPayable || 0).toFixed(2)}</td></tr>
            </tbody>
          </table>
          ${slip.notes ? `<p style="margin-top:15px; font-size:12px; color:#64748b;"><strong>Notes:</strong> ${slip.notes}</p>` : ''}
          <div class="signature">
            <div>___________________<br/>Staff Signature</div>
            <div>___________________<br/>Admin Seal & Sign</div>
          </div>
        </body>
      </html>
    `;
    printWindow.document.write(content);
    printWindow.document.close();
    setTimeout(() => printWindow.print(), 250);
  };

  const fmt = (num) => Number.isFinite(Number(num)) ? Number(num).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00';
  const filteredStaff = staffList.filter(s => s.name.toLowerCase().includes(searchQuery.toLowerCase()) || (s.staffId || '').toLowerCase().includes(searchQuery.toLowerCase()));

  const styles = {
    container: { minHeight: '100vh', background: '#f4f9fd', fontFamily: "'Inter', sans-serif" },
    header: { background: '#ffffff', borderBottom: '1px solid #e0f2fe', padding: isMobile ? '12px' : isTablet ? '16px 24px' : '20px 32px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', gap: isMobile ? 12 : isTablet ? 16 : 0, position: 'sticky', top: 0, zIndex: 100 },
    headerTitle: { display: 'flex', alignItems: 'center', gap: isMobile ? 8 : 12 },
    headerIcon: { width: isMobile ? 32 : isTablet ? 36 : 40, height: isMobile ? 32 : isTablet ? 36 : 40, background: 'linear-gradient(135deg, #1880c2 0%, #0ea5e9 100%)', borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', fontSize: isMobile ? 16 : 18, boxShadow: '0 4px 10px rgba(24, 128, 194, 0.2)' },
    headerText: { fontSize: isMobile ? 16 : isTablet ? 18 : 20, fontWeight: 700, color: '#1a202c' },
    headerSubtext: { fontSize: isMobile ? 11 : 13, color: '#64748b', display: isSmallMobile ? 'none' : 'block' },
    headerActions: { display: 'flex', alignItems: 'center', gap: isMobile ? 6 : 10, width: isMobile ? '100%' : 'auto', flexWrap: 'wrap', flexDirection: isMobile ? 'column' : 'row' },
    
    tabBtn: (active) => ({
      padding: isMobile ? '8px 16px' : '10px 20px', 
      background: active ? 'linear-gradient(135deg, #1880c2 0%, #0ea5e9 100%)' : '#f4f9fd',
      color: active ? 'white' : '#64748b',
      border: active ? 'none' : '1px solid #e0f2fe', 
      borderRadius: 8, fontSize: isMobile ? 12 : 14, fontWeight: 600, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, flex: isMobile ? '1' : 'none', justifyContent: 'center', whiteSpace: 'nowrap', transition: 'all 0.2s', boxShadow: active ? '0 4px 10px rgba(24, 128, 194, 0.2)' : 'none'
    }),
    
    main: { maxWidth: 1400, margin: '0 auto', padding: isMobile ? '12px' : isTablet ? '16px 24px' : '24px 32px' },
    card: { background: '#ffffff', borderRadius: 12, border: '1px solid #e0f2fe', marginBottom: isMobile ? 16 : 24, overflow: 'hidden', boxShadow: '0 4px 16px rgba(24, 128, 194, 0.08)' },
    cardHeader: { padding: isMobile ? '12px 16px' : '16px 24px', borderBottom: '1px solid #e0f2fe', display: 'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent: 'space-between', alignItems: isMobile ? 'flex-start' : 'center', background: '#fff', gap: isMobile ? 8 : 0 },
    cardTitle: { fontSize: isMobile ? 14 : 16, fontWeight: 700, color: '#1a202c', display: 'flex', alignItems: 'center', gap: 10 },
    cardBody: { padding: isMobile ? 16 : 24 },
    
    input: { width: '100%', padding: isMobile ? '8px 12px' : '10px 14px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: isMobile ? 12 : 14, boxSizing: 'border-box', outline: 'none', transition: 'border 0.2s', color: '#1a202c' },
    select: { width: '100%', padding: isMobile ? '8px 12px' : '10px 14px', borderRadius: 8, border: '1px solid #cbd5e1', fontSize: isMobile ? 12 : 14, boxSizing: 'border-box', outline: 'none', background: 'white', color: '#1a202c' },
    label: { display: 'block', fontSize: isMobile ? 10 : 11, fontWeight: 700, color: '#64748b', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' },
    
    btnPrimary: { padding: isMobile ? '8px 16px' : '10px 24px', background: 'linear-gradient(135deg, #1880c2 0%, #0ea5e9 100%)', color: 'white', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: isMobile ? 12 : 14, justifyContent: 'center', boxShadow: '0 4px 10px rgba(24, 128, 194, 0.2)', transition: '0.2s' },
    btnSuccess: { padding: isMobile ? '6px 12px' : '8px 16px', background: '#ecfdf5', color: '#10b981', border: '1px solid #a7f3d0', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: isMobile ? 10 : 12 },
    btnSecondary: { padding: isMobile ? '8px 16px' : '10px 20px', background: '#f4f9fd', color: '#1880c2', border: '1px solid #bae6fd', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: isMobile ? 12 : 14, transition: '0.2s' },
    btnDanger: { padding: isMobile ? '6px 12px' : '8px 14px', background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 8, fontWeight: 600, cursor: 'pointer', fontSize: isMobile ? 10 : 12 },
    
    table: { width: '100%', borderCollapse: 'collapse' },
    th: { padding: isMobile ? '8px 12px' : '12px 16px', textAlign: 'left', fontSize: isMobile ? 10 : 11, fontWeight: 700, color: '#64748b', background: '#f4f9fd', borderBottom: '1px solid #e0f2fe', textTransform: 'uppercase', letterSpacing: '0.5px' },
    td: { padding: isMobile ? '8px 12px' : '12px 16px', fontSize: isMobile ? 12 : 14, color: '#1a202c', borderBottom: '1px solid #e0f2fe' },
    
    modal: { position: 'fixed', inset: 0, background: 'rgba(15, 23, 42, 0.6)', backdropFilter: 'blur(4px)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: isMobile ? 16 : 20 },
    modalContent: { background: '#ffffff', borderRadius: 12, width: isMobile ? '100%' : isTablet ? '90%' : '600px', maxHeight: isMobile ? '90vh' : '90vh', overflowY: 'auto', boxShadow: '0 25px 50px rgba(0,0,0,0.25)', padding: isMobile ? 16 : 24, border: '1px solid #e0f2fe' },
    
    actionBtn: { padding: isMobile ? '4px 8px' : '6px 12px', borderRadius: 6, fontSize: isMobile ? 10 : 12, fontWeight: 600, cursor: 'pointer', marginRight: 6, border: 'none' },
    editBtn: { background: '#e0f2fe', color: '#1880c2' },
    deleteBtn: { background: '#fef2f2', color: '#dc2626' }
  };

  return (
    <div style={styles.container}>
      <header style={styles.header}>
        <div style={styles.headerTitle}>
          <div style={styles.headerIcon}>👥</div>
          <div>
            <div style={styles.headerText}>HR & Staff Payroll</div>
            <div style={styles.headerSubtext}>Manage staff details, attendances and payroll slips</div>
          </div>
        </div>
        <div style={styles.headerActions}>
          {isMobile && (
            <div style={{ display: 'flex', gap: 6, width: '100%', flexDirection: isSmallMobile ? 'column' : 'row' }}>
              <button onClick={() => setActiveTab('directory')} style={styles.tabBtn(activeTab==='directory')}>
                <span>👥</span> {isSmallMobile ? 'Staff' : 'Directory'}
              </button>
              <button onClick={() => setActiveTab('attendance')} style={styles.tabBtn(activeTab==='attendance')}>
                <span>⏰</span> {isSmallMobile ? 'Time' : 'Attendance'}
              </button>
              <button onClick={() => setActiveTab('leaves')} style={styles.tabBtn(activeTab==='leaves')}>
                <span>📂</span> {isSmallMobile ? 'Leave' : 'Leaves'}
              </button>
              {(user?.role === 'admin' || user?.role === 'superadmin') && (
                <button onClick={() => setActiveTab('payroll')} style={styles.tabBtn(activeTab==='payroll')}>
                  <span>💰</span> {isSmallMobile ? 'Pay' : 'Payroll'}
                </button>
              )}
            </div>
          )}
          
          {!isMobile && (
            <>
              <button onClick={() => setActiveTab('directory')} style={styles.tabBtn(activeTab==='directory')}>
                <span>👥</span> Staff Directory
              </button>
              <button onClick={() => setActiveTab('attendance')} style={styles.tabBtn(activeTab==='attendance')}>
                <span>⏰</span> Attendance Logs
              </button>
              <button onClick={() => setActiveTab('leaves')} style={styles.tabBtn(activeTab==='leaves')}>
                <span>📂</span> Leave Requests
              </button>
              {(user?.role === 'admin' || user?.role === 'superadmin') && (
                <button onClick={() => setActiveTab('payroll')} style={styles.tabBtn(activeTab==='payroll')}>
                  <span>💰</span> Payslips & Payroll
                </button>
              )}
            </>
          )}
        </div>
      </header>

      <main style={styles.main}>
        {activeTab === 'directory' && (
          <div>
            {user?.role === 'staff' && (
              <div style={{ ...styles.card, background: 'linear-gradient(135deg, #e0f2fe 0%, #bae6fd 100%)', borderColor: '#7dd3fc' }}>
                <div style={styles.cardBody}>
                  <div style={{ display:'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent:'space-between', alignItems: isMobile ? 'stretch' : 'center', gap:16 }}>
                    <div>
                      <h3 style={{margin:0, color:'#0369a1', fontSize: isMobile ? 16 : 18}}>Staff Self Service</h3>
                      <p style={{ margin:'4px 0 0', color:'#0284c7', fontSize: isMobile ? 11 : 13 }}>
                        Log daily check-in and check-out times
                      </p>
                    </div>
                    <div style={{ display:'flex', gap: isMobile ? 8 : 12, flexDirection: isMobile ? 'column' : 'row' }}>
                      <button onClick={handleCheckIn} style={{ ...styles.btnPrimary, padding: isMobile ? '10px 20px' : '12px 28px' }}>
                        Clock In
                      </button>
                      <button onClick={handleCheckOut} style={{ ...styles.btnSecondary, background:'#fff', padding: isMobile ? '10px 20px' : '12px 28px' }}>
                        Clock Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {user?.role === 'admin' && (
              <div style={{ ...styles.card, padding: 16, display:'flex', flexDirection: isMobile ? 'column' : 'row', justifyContent:'space-between', alignItems: isMobile ? 'stretch' : 'center', gap:12, marginBottom:16 }}>
                <div style={{flex:1}}>
                  <input
                    value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                    placeholder="Search staff by name or ID..."
                    style={{ ...styles.input, maxWidth: isMobile ? 'none' : 300 }}
                  />
                </div>
                <button 
                  onClick={() => {
                    setModalMode('create');
                    setFormData({ name:'', email:'', phone:'', password:'', permissions:[], baseSalary:'', allowance:'', deduction:'' });
                    setShowModal(true);
                  }} 
                  style={styles.btnPrimary}
                >
                  {isMobile ? 'Add Staff' : 'Register Staff'}
                </button>
              </div>
            )}

            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}>
                  <span>📋</span> Active Staff Directory
                </div>
              </div>
              <div style={{ overflowX:'auto', marginBottom: isMobile ? 16 : 0 }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Staff Name</th>
                      <th style={styles.th}>Gender / Shift</th>
                      <th style={styles.th}>Staff ID</th>
                      <th style={styles.th}>Email / Phone</th>
                      <th style={styles.th}>Permissions</th>
                      {user?.role === 'admin' && <th style={styles.th}>Salary config</th>}
                      {user?.role === 'admin' && <th style={styles.th}>Status</th>}
                      {user?.role === 'admin' && <th style={styles.th}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filteredStaff.map(s => (
                      <tr key={s._id}>
                        <td style={{...styles.td, fontWeight:600}}>{s.name}</td>
                        <td style={styles.td}>
                          <div style={{ fontSize: isMobile ? 11 : 14 }}>{s.gender || 'Male'}</div>
                          <span style={{ fontSize: isMobile ? 9 : 11, background:'#fef3c7', color:'#92400e', padding:'2px 6px', borderRadius:4, fontWeight:700 }}>
                            {s.shift || 'Morning'}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <span style={{ fontFamily:'monospace', background:'#f4f9fd', border: '1px solid #e0f2fe', padding:'4px 8px', borderRadius:6, fontSize: isMobile ? 10 : 12, color: '#1880c2' }}>
                            {s.staffId}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ fontSize: isMobile ? 11 : 14 }}>{s.email}</div>
                          <div style={{fontSize: isMobile ? 10 : 12, color:'#64748b'}}>{s.phone || '—'}</div>
                        </td>
                        <td style={styles.td}>
                          <div style={{display:'flex', flexWrap:'wrap', gap:4}}>
                            {(s.permissions || []).slice(0, isMobile ? 2 : 3).map(p => (
                              <span key={p} style={{ fontSize: isMobile ? 8 : 10, background:'#e0f2fe', color:'#1880c2', padding:'2px 6px', borderRadius:4, fontWeight: 600 }}>
                                {p}
                              </span>
                            ))}
                            {(s.permissions || []).length > (isMobile ? 2 : 3) && (
                              <span style={{ fontSize: isMobile ? 8 : 10, background:'#f1f5f9', color:'#64748b', padding:'2px 6px', borderRadius:4 }}>
                                +{(s.permissions || []).length - (isMobile ? 2 : 3)}
                              </span>
                            )}
                          </div>
                        </td>
                        {user?.role === 'admin' && (
                          <td style={styles.td}>
                            {s.salaryConfig ? (
                              <div style={{fontSize: isMobile ? 10 : 12}}>
                                <div style={{fontWeight:600}}>Base: Rs {fmt(s.salaryConfig.baseSalary)}</div>
                                <div style={{color:'#94a3b8', fontSize: isMobile ? 9 : 11}}>
                                  Alw: Rs {fmt(s.salaryConfig.allowance)} | Ded: Rs {fmt(s.salaryConfig.deduction)}
                                </div>
                              </div>
                            ) : '—'}
                          </td>
                        )}
                        {user?.role === 'admin' && (
                          <td style={styles.td}>
                            <button
                              onClick={() => toggleStaffStatus(s._id)}
                              style={{ padding: isMobile ? '3px 6px' : '4px 8px', background: s.isActive ? '#ecfdf4' : '#fef2f2', color: s.isActive ? '#10b981' : '#ef4444', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: isMobile ? 10 : 12 }}
                            >
                              {s.isActive ? 'Active' : 'Inactive'}
                            </button>
                          </td>
                        )}
                        {user?.role === 'admin' && (
                          <td style={styles.td}>
                            <div style={{ display: 'flex', gap: 4, flexDirection: isMobile ? 'column' : 'row' }}>
                              <button onClick={() => handleEditStaff(s)} style={{...styles.actionBtn, ...styles.editBtn}}>
                                Edit
                              </button>
                              <button onClick={() => handleDeleteStaff(s._id)} style={{...styles.actionBtn, ...styles.deleteBtn}}>
                                Del
                              </button>
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                    {filteredStaff.length === 0 && (
                      <tr>
                        <td colSpan="8" style={{ ...styles.td, textAlign:'center', padding: isMobile ? '24px' : '32px', color:'#94a3b8' }}>
                          No staff registered.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'attendance' && (
          <div>
            <div style={{ ...styles.card, padding:16, display:'flex', gap:16, flexWrap:'wrap', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ flex: isMobile ? '1' : 'none' }}>
                <label style={styles.label}>Select Month</label>
                <input type="month" value={selectedMonth} onChange={e => setSelectedMonth(e.target.value)} style={styles.input} />
              </div>
              {user?.role === 'admin' && (
                <div style={{ flex: isMobile ? '1' : 'none' }}>
                  <label style={styles.label}>Filter by Staff</label>
                  <select value={attendanceStaffId} onChange={e => setAttendanceStaffId(e.target.value)} style={styles.select}>
                    <option value="all">-- All Staff --</option>
                    {staffList.map(s => (
                      <option key={s._id} value={s._id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              )}
              <div style={{ display:'flex', gap:12, marginTop: isMobile ? 0 : 18, flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                <button onClick={fetchAttendance} style={{ ...styles.btnSecondary, height: isMobile ? 'auto' : 42 }}>
                  Apply Filters
                </button>
                {user?.role === 'admin' && (
                  <button 
                    onClick={() => {
                      setManualAttendance({ user: '', date: new Date().toISOString().split('T')[0], checkIn: '', checkOut: '', status: 'Present', notes: '' });
                      setShowManualAttendanceModal(true);
                    }} 
                    style={{ ...styles.btnPrimary, height: isMobile ? 'auto' : 42 }}
                  >
                    {isMobile ? 'Manual Entry' : 'Manual Entry'}
                  </button>
                )}
              </div>
            </div>

            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}>
                  <span>⏰</span> Daily Attendance logs
                </div>
              </div>
              <div style={{ overflowX:'auto', marginBottom: isMobile ? 16 : 0 }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Date</th>
                      <th style={styles.th}>Staff Member</th>
                      <th style={styles.th}>Check-In</th>
                      <th style={styles.th}>Check-Out</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Particulars / Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendanceRecords.map(r => (
                      <tr key={r._id}>
                        <td style={styles.td}>{new Date(r.date).toLocaleDateString('en-IN')}</td>
                        <td style={{...styles.td, fontWeight:600}}>{r.user?.name || 'Unknown Staff'}</td>
                        <td style={styles.td}>{r.checkIn ? new Date(r.checkIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td style={styles.td}>{r.checkOut ? new Date(r.checkOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '—'}</td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '4px 8px', borderRadius: 6, fontSize: isMobile ? 10 : 12, fontWeight: 700,
                            background: r.status === 'Present' ? '#ecfdf4' : r.status === 'Late' ? '#fffbeb' : r.status === 'Leave' ? '#e0f2fe' : '#fef2f2',
                            color: r.status === 'Present' ? '#10b981' : r.status === 'Late' ? '#b45309' : r.status === 'Leave' ? '#1880c2' : '#ef4444',
                            border: `1px solid ${r.status === 'Present' ? '#a7f3d0' : r.status === 'Late' ? '#fde68a' : r.status === 'Leave' ? '#bae6fd' : '#fecaca'}`
                          }}>
                            {r.status}
                          </span>
                        </td>
                        <td style={{...styles.td, maxWidth: isMobile ? '150px' : 'none', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                          {r.notes || '—'}
                        </td>
                      </tr>
                    ))}
                    {attendanceRecords.length === 0 && (
                      <tr>
                        <td colSpan="6" style={{ ...styles.td, textAlign:'center', padding: isMobile ? '24px' : '32px', color:'#94a3b8' }}>
                          No attendance records found for this period.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'payroll' && user?.role === 'admin' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: isSmallMobile ? '1fr' : isMobile ? 'repeat(2, 1fr)' : 'repeat(3, 1fr)', gap: isMobile ? 12 : 16, marginBottom: isMobile ? 12 : 16 }}>
              <div style={{ background: '#ffffff', border: '1px solid #e0f2fe', borderRadius: 12, padding: isMobile ? 12 : 16, boxShadow: '0 4px 16px rgba(24, 128, 194, 0.08)' }}>
                <div style={{ fontSize: isMobile ? 10 : 11, color: '#64748b', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Total Monthly Salary Pool</div>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#1a202c', marginTop: 4 }}>Rs {fmt(payslips.reduce((s, x) => s + (x.netPayable || 0), 0))}</div>
                <div style={{ fontSize: isMobile ? 10 : 12, color: '#94a3b8', marginTop: 4 }}>For {payrollMonth} ({formatAdToBs(`${payrollMonth}-01`, 'short')})</div>
              </div>
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 12, padding: isMobile ? 12 : 16, boxShadow: '0 4px 16px rgba(22, 101, 52, 0.05)' }}>
                <div style={{ fontSize: isMobile ? 10 : 11, color: '#166534', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Disbursed Amount</div>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#15803d', marginTop: 4 }}>Rs {fmt(payslips.filter(x => x.status === 'Paid').reduce((s, x) => s + (x.netPayable || 0), 0))}</div>
                <div style={{ fontSize: isMobile ? 10 : 12, color: '#166534', marginTop: 4 }}>{payslips.filter(x => x.status === 'Paid').length} Staff Paid</div>
              </div>
              <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 12, padding: isMobile ? 12 : 16, gridColumn: isSmallMobile ? '1' : isMobile ? 'span 2' : 'auto', boxShadow: '0 4px 16px rgba(180, 83, 9, 0.05)' }}>
                <div style={{ fontSize: isMobile ? 10 : 11, color: '#b45309', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px' }}>Pending Payout</div>
                <div style={{ fontSize: isMobile ? 18 : 22, fontWeight: 700, color: '#b45309', marginTop: 4 }}>Rs {fmt(payslips.filter(x => x.status === 'Unpaid').reduce((s, x) => s + (x.netPayable || 0), 0))}</div>
                <div style={{ fontSize: isMobile ? 10 : 12, color: '#b45309', marginTop: 4 }}>{payslips.filter(x => x.status === 'Unpaid').length} Staff Pending</div>
              </div>
            </div>

            <div style={{ ...styles.card, padding:16, display:'flex', justifyContent:'space-between', alignItems: isMobile ? 'stretch' : 'center', flexWrap:'wrap', gap:12, flexDirection: isMobile ? 'column' : 'row' }}>
              <div style={{ display:'flex', gap:12, alignItems:'center', flexDirection: isMobile ? 'column' : 'row', width: isMobile ? '100%' : 'auto' }}>
                <span style={{ fontWeight:700, color:'#64748b', fontSize: isMobile ? 12 : 14 }}>Month:</span>
                <input type="month" value={payrollMonth} onChange={e => setPayrollMonth(e.target.value)} style={{ ...styles.input, maxWidth: isMobile ? 'none' : 200 }} />
                <button onClick={fetchPayslips} style={styles.btnSecondary}>Load Payroll</button>
              </div>
              <button onClick={handleGeneratePayroll} style={styles.btnPrimary}>
                {isMobile ? 'Generate Payroll' : 'Generate Monthly Payroll'}
              </button>
            </div>

            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}><span>💰</span> Staff Payroll sheet</div>
              </div>
              <div style={{ overflowX:'auto', marginBottom: isMobile ? 16 : 0 }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Staff Member</th>
                      <th style={styles.th}>Base Salary</th>
                      <th style={styles.th}>Allowances</th>
                      <th style={styles.th}>Deductions</th>
                      <th style={styles.th}>Net Salary</th>
                      <th style={styles.th}>Status</th>
                      <th style={styles.th}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {payslips.map(slip => (
                      <tr key={slip._id}>
                        <td style={{...styles.td, fontWeight:600}}>{slip.userName}</td>
                        <td style={styles.td}>Rs {fmt(slip.baseSalary)}</td>
                        <td style={{...styles.td, color:'#10b981', fontWeight: 600}}>+ Rs {fmt(slip.allowances)}</td>
                        <td style={{...styles.td, color:'#ef4444', fontWeight: 600}}>- Rs {fmt(slip.deductions)}</td>
                        <td style={{...styles.td, fontWeight:700, color: '#1880c2'}}>Rs {fmt(slip.netPayable)}</td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '4px 8px', borderRadius: 6, fontSize: isMobile ? 10 : 12, fontWeight: 700,
                            background: slip.status === 'Paid' ? '#ecfdf4' : '#fffbeb',
                            color: slip.status === 'Paid' ? '#10b981' : '#b45309',
                            border: `1px solid ${slip.status === 'Paid' ? '#a7f3d0' : '#fde68a'}`
                          }}>
                            {slip.status}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap', flexDirection: isMobile ? 'column' : 'row' }}>
                            {slip.status === 'Unpaid' && (
                              <button onClick={() => { setPayoutSlip(slip); setShowPayoutModal(true); }} style={styles.btnSuccess}>
                                {isMobile ? 'Pay' : 'Disburse Pay'}
                              </button>
                            )}
                            <button onClick={() => printPayslip(slip)} style={styles.btnSecondary} title="Print Salary Advice / Slip">
                              🖨️ {isMobile ? 'Print' : 'Print Slip'}
                            </button>
                            {slip.status === 'Unpaid' && (
                              <button onClick={() => handleDeletePayslip(slip._id)} style={styles.btnDanger}>Delete</button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                    {payslips.length === 0 && (
                      <tr>
                        <td colSpan="7" style={{ ...styles.td, textAlign:'center', padding: isMobile ? '24px' : '32px', color:'#94a3b8' }}>
                          No payslips generated for this month.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'leaves' && (
          <div>
            <div style={{ ...styles.card, padding: 16, display:'flex', justifyContent:'space-between', alignItems: isMobile ? 'stretch' : 'center', flexWrap:'wrap', gap:12, marginBottom:16, flexDirection: isMobile ? 'column' : 'row' }}>
              <div>
                <h3 style={{ margin:0, color:'#1a202c', fontSize: isMobile ? 16 : 18 }}>Leave Requests Log</h3>
                <p style={{ margin:'4px 0 0', color:'#64748b', fontSize: isMobile ? 11 : 13 }}>Submit or manage staff absence and sick leaves</p>
              </div>
              <button 
                onClick={() => {
                  setLeaveFormData({ startDate: new Date().toISOString().split('T')[0], endDate: new Date().toISOString().split('T')[0], type: 'Unpaid', reason: '' });
                  setShowLeaveModal(true);
                }} 
                style={styles.btnPrimary}
              >
                {isMobile ? 'Request Leave' : 'Request Leave'}
              </button>
            </div>

            <div style={styles.card}>
              <div style={styles.cardHeader}>
                <div style={styles.cardTitle}><span>📂</span> Leave Application History</div>
              </div>
              <div style={{ overflowX:'auto', marginBottom: isMobile ? 16 : 0 }}>
                <table style={styles.table}>
                  <thead>
                    <tr>
                      <th style={styles.th}>Employee</th>
                      <th style={styles.th}>Type</th>
                      <th style={styles.th}>Dates</th>
                      <th style={styles.th}>Reason</th>
                      <th style={styles.th}>Status</th>
                      {(user?.role === 'admin' || user?.role === 'superadmin') && <th style={styles.th}>Actions</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {leavesList.map(leave => (
                      <tr key={leave._id}>
                        <td style={{...styles.td, fontWeight:600}}>{leave.userName}</td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '4px 8px', borderRadius: 6, fontSize: isMobile ? 9 : 11, fontWeight: 700,
                            background: leave.type === 'Sick' ? '#fef2f2' : leave.type === 'Annual' ? '#e0f2fe' : '#f1f5f9',
                            color: leave.type === 'Sick' ? '#dc2626' : leave.type === 'Annual' ? '#1880c2' : '#475569'
                          }}>
                            {leave.type}
                          </span>
                        </td>
                        <td style={styles.td}>
                          <div style={{fontWeight:600, fontSize: isMobile ? 11 : 14}}>
                            {new Date(leave.startDate).toLocaleDateString()} to {new Date(leave.endDate).toLocaleDateString()}
                          </div>
                          <div style={{fontSize: isMobile ? 9 : 11, color:'#94a3b8'}}>
                            {Math.ceil((new Date(leave.endDate) - new Date(leave.startDate)) / (1000*60*60*24)) + 1} day(s)
                          </div>
                        </td>
                        <td style={{...styles.td, maxWidth: isMobile ? '150px' : 'none', overflow: 'hidden', textOverflow: 'ellipsis'}}>{leave.reason || '—'}</td>
                        <td style={styles.td}>
                          <span style={{
                            padding: '4px 8px', borderRadius: 6, fontSize: isMobile ? 10 : 12, fontWeight: 700,
                            background: leave.status === 'Approved' ? '#ecfdf5' : leave.status === 'Rejected' ? '#fef2f2' : '#fffbeb',
                            color: leave.status === 'Approved' ? '#10b981' : leave.status === 'Rejected' ? '#ef4444' : '#b45309',
                            border: `1px solid ${leave.status === 'Approved' ? '#a7f3d0' : leave.status === 'Rejected' ? '#fecaca' : '#fde68a'}`
                          }}>
                            {leave.status}
                          </span>
                        </td>
                        {(user?.role === 'admin' || user?.role === 'superadmin') && (
                          <td style={styles.td}>
                            {leave.status === 'Pending' ? (
                              <div style={{ display:'flex', gap:6, flexDirection: isMobile ? 'column' : 'row' }}>
                                <button onClick={() => handleUpdateLeaveStatus(leave._id, 'Approved')} style={styles.btnSuccess}>{isMobile ? 'App' : 'Approve'}</button>
                                <button onClick={() => handleUpdateLeaveStatus(leave._id, 'Rejected')} style={styles.btnDanger}>{isMobile ? 'Rej' : 'Reject'}</button>
                              </div>
                            ) : (
                              <span style={{ color:'#94a3b8', fontSize: isMobile ? 10 : 12, fontWeight:600 }}>Processed</span>
                            )}
                          </td>
                        )}
                      </tr>
                    ))}
                    {leavesList.length === 0 && (
                      <tr>
                        <td colSpan={6} style={{ ...styles.td, textAlign:'center', padding: isMobile ? '24px' : '32px', color:'#94a3b8' }}>No leave applications found.</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </main>

      {showLeaveModal && (
        <div style={styles.modal} onClick={() => setShowLeaveModal(false)}>
          <form onSubmit={handleLeaveSubmit} style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 16px', fontSize: isMobile ? 16 : 18, color: '#1a202c' }}>Apply for Leave</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom:24 }}>
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12 }}>
                <div>
                  <label style={styles.label}>Start Date</label>
                  <input type="date" value={leaveFormData.startDate} onChange={e => setLeaveFormData({...leaveFormData, startDate: e.target.value})} style={styles.input} required />
                </div>
                <div>
                  <label style={styles.label}>End Date</label>
                  <input type="date" value={leaveFormData.endDate} onChange={e => setLeaveFormData({...leaveFormData, endDate: e.target.value})} style={styles.input} required />
                </div>
              </div>
              <div>
                <label style={styles.label}>Leave Type</label>
                <select value={leaveFormData.type} onChange={e => setLeaveFormData({...leaveFormData, type: e.target.value})} style={styles.select}>
                  <option value="Annual">Annual Leave</option>
                  <option value="Sick">Sick Leave</option>
                  <option value="Casual">Casual Leave</option>
                  <option value="Unpaid">Unpaid Leave (payroll deduction applies)</option>
                </select>
              </div>
              <div>
                <label style={styles.label}>Reason / Description</label>
                <textarea value={leaveFormData.reason} onChange={e => setLeaveFormData({...leaveFormData, reason: e.target.value})} placeholder="Explain why leave is required..." style={{ ...styles.input, minHeight: isMobile ? 60 : 80, resize:'vertical' }} required />
              </div>
            </div>
            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
              <button type="button" onClick={() => setShowLeaveModal(false)} style={styles.btnSecondary}>Cancel</button>
              <button type="submit" style={styles.btnPrimary}>Submit Request</button>
            </div>
          </form>
        </div>
      )}

      {showModal && (
        <div style={styles.modal} onClick={() => setShowModal(false)}>
          <form onSubmit={handleFormSubmit} style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 16px', fontSize: isMobile ? 16 : 18, color: '#1a202c' }}>
              {modalMode === 'create' ? 'Register Staff Account' : 'Edit Staff Account'}
            </h3>
            <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12, marginBottom:16 }}>
              <div><label style={styles.label}>Name *</label><input value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} style={styles.input} required /></div>
              <div><label style={styles.label}>Email *</label><input type="email" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} style={styles.input} required /></div>
              <div><label style={styles.label}>Phone</label><input value={formData.phone} onChange={e => setFormData({...formData, phone: e.target.value})} style={styles.input} /></div>
              {modalMode === 'create' && (<div><label style={styles.label}>Password *</label><input type="password" value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} style={styles.input} required /></div>)}
              <div><label style={styles.label}>Gender</label><select value={formData.gender} onChange={e => setFormData({...formData, gender: e.target.value})} style={styles.select}><option value="Male">Male</option><option value="Female">Female</option><option value="Other">Other</option></select></div>
              <div><label style={styles.label}>Duty Shift</label><select value={formData.shift} onChange={e => setFormData({...formData, shift: e.target.value})} style={styles.select}><option value="Morning">Morning Shift</option><option value="Evening">Evening Shift</option><option value="Night">Night Shift</option><option value="Flexible">Flexible Shift</option></select></div>
            </div>

            <div style={{ borderTop:'1px dashed #cbd5e1', paddingTop:16, marginBottom:16 }}>
              <h4 style={{ margin:'0 0 12px', fontSize: isMobile ? 12 : 13, color:'#475569' }}>Salary Configuration</h4>
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 1fr', gap:10 }}>
                <div><label style={styles.label}>Base Salary (Monthly)</label><input type="number" value={formData.baseSalary} onChange={e => setFormData({...formData, baseSalary: e.target.value})} style={styles.input} placeholder="Rs 0" /></div>
                <div><label style={styles.label}>Allowance</label><input type="number" value={formData.allowance} onChange={e => setFormData({...formData, allowance: e.target.value})} style={styles.input} placeholder="Rs 0" /></div>
                <div><label style={styles.label}>Deduction</label><input type="number" value={formData.deduction} onChange={e => setFormData({...formData, deduction: e.target.value})} style={styles.input} placeholder="Rs 0" /></div>
              </div>
            </div>

            <div style={{ borderTop:'1px dashed #cbd5e1', paddingTop:16, marginBottom:20 }}>
              <label style={styles.label}>Account Permissions</label>
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:10, maxHeight: isMobile ? 120 : 150, overflowY:'auto', border:'1px solid #cbd5e1', borderRadius:8, padding:10, background:'#f4f9fd' }}>
                {availablePermissions.map(p => (
                  <label key={p.key} style={{ display:'flex', alignItems:'center', gap:8, fontSize: isMobile ? 11 : 13, cursor:'pointer' }}>
                    <input type="checkbox" checked={formData.permissions.includes(p.key)} onChange={e => { const nextPermissions = e.target.checked ? [...formData.permissions, p.key] : formData.permissions.filter(key => key !== p.key); setFormData({...formData, permissions: nextPermissions}); }} />
                    <span>{p.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
              <button type="button" onClick={() => setShowModal(false)} style={styles.btnSecondary}>Cancel</button>
              <button type="submit" style={styles.btnPrimary}>Save Staff</button>
            </div>
          </form>
        </div>
      )}

      {showPayoutModal && payoutSlip && (
        <div style={styles.modal} onClick={() => setShowPayoutModal(false)}>
          <form onSubmit={handleConfirmPayout} style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 8px', fontSize: isMobile ? 16 : 18, color: '#1a202c' }}>Confirm Payroll Disbursement</h3>
            <p style={{ margin:'0 0 20px', color:'#64748b', fontSize: isMobile ? 12 : 14 }}>Record payout details for {payoutSlip.userName}.</p>
            <div style={{ background:'#f4f9fd', padding:16, borderRadius:12, marginBottom:20, border:'1px solid #e0f2fe' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize: isMobile ? 12 : 14, flexDirection: isMobile ? 'column' : 'row' }}><span>Staff Name:</span><strong>{payoutSlip.userName}</strong></div>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:8, fontSize: isMobile ? 12 : 14, flexDirection: isMobile ? 'column' : 'row' }}><span>Payroll Month:</span><strong>{payoutSlip.month}</strong></div>
              <div style={{ display:'flex', justifyContent:'space-between', borderTop:'1px dashed #cbd5e1', paddingTop:8, fontSize: isMobile ? 14 : 16, flexDirection: isMobile ? 'column' : 'row' }}><strong>Net Payable:</strong><strong style={{color:'#1880c2'}}>Rs {fmt(payoutSlip.netPayable)}</strong></div>
            </div>
            <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom:24 }}>
              <div><label style={styles.label}>Payment Method</label><select value={payoutDetails.paymentType} onChange={e => setPayoutDetails({...payoutDetails, paymentType: e.target.value})} style={styles.select}><option value="CASH">Cash payout</option><option value="ONLINE">Bank Online transfer</option></select></div>
              <div><label style={styles.label}>Notes</label><input value={payoutDetails.notes} onChange={e => setPayoutDetails({...payoutDetails, notes: e.target.value})} placeholder="e.g. Bank slip transfer, cleared salary..." style={styles.input} /></div>
            </div>
            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
              <button type="button" onClick={() => setShowPayoutModal(false)} style={styles.btnSecondary}>Cancel</button>
              <button type="submit" style={styles.btnPrimary}>Confirm Payment</button>
            </div>
          </form>
        </div>
      )}

      {showManualAttendanceModal && (
        <div style={styles.modal} onClick={() => setShowManualAttendanceModal(false)}>
          <form onSubmit={handleManualAttendanceSubmit} style={styles.modalContent} onClick={e => e.stopPropagation()}>
            <h3 style={{ margin:'0 0 16px', fontSize: isMobile ? 16 : 18, color: '#1a202c' }}>Manual Attendance Adjust</h3>
            <div style={{ display:'flex', flexDirection:'column', gap:16, marginBottom:20 }}>
              <div><label style={styles.label}>Select Staff Member</label><select value={manualAttendance.user} onChange={e => setManualAttendance({...manualAttendance, user: e.target.value})} style={styles.select} required><option value="">-- Choose Employee --</option>{staffList.map(s => (<option key={s._id} value={s._id}>{s.name}</option>))}</select></div>
              <div><label style={styles.label}>Date</label><input type="date" value={manualAttendance.date} onChange={e => setManualAttendance({...manualAttendance, date: e.target.value})} style={styles.input} required /></div>
              <div style={{ display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap:12 }}>
                <div><label style={styles.label}>Clock In time</label><input type="datetime-local" value={manualAttendance.checkIn} onChange={e => setManualAttendance({...manualAttendance, checkIn: e.target.value})} style={styles.input} /></div>
                <div><label style={styles.label}>Clock Out time</label><input type="datetime-local" value={manualAttendance.checkOut} onChange={e => setManualAttendance({...manualAttendance, checkOut: e.target.value})} style={styles.input} /></div>
              </div>
              <div><label style={styles.label}>Duty Attendance Status</label><select value={manualAttendance.status} onChange={e => setManualAttendance({...manualAttendance, status: e.target.value})} style={styles.select}><option value="Present">Present</option><option value="Late">Late (Arrived late)</option><option value="Leave">On Leave</option><option value="Absent">Absent</option></select></div>
              <div><label style={styles.label}>Adjust notes</label><input value={manualAttendance.notes} onChange={e => setManualAttendance({...manualAttendance, notes: e.target.value})} placeholder="Notes (sick leave, adjustments)..." style={styles.input} /></div>
            </div>
            <div style={{ display:'flex', gap:12, justifyContent:'flex-end', flexDirection: isMobile ? 'column' : 'row' }}>
              <button type="button" onClick={() => setShowManualAttendanceModal(false)} style={styles.btnSecondary}>Cancel</button>
              <button type="submit" style={styles.btnPrimary}>Save Entry</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}